import React, {
  Fragment,
  memo,
  useRef,
  useEffect,
  useState,
  useMemo,
} from "react";
import { Typography, Box } from "@mui/material";
import { itemCheckDiscountLabel } from "src/helper/checkOrderSummary";
import { buildRollupLine, formatModifierOption } from "src/helper/lineTotal";
import {
  couponDetailLabel,
  promotionAdjustmentLabel,
} from "src/helper/promotionItemAttribution";
import { valueFormat } from "src/helper/valueFormat";
import {
  DiscountRowLabelWrapper,
  DiscountRowPriceWrapper,
  DiscountRowTextWrapper,
  NamePriceRowWrapper,
  DiscountRowWrapper,
  MofiferNameWrapper,
  MofiferOptionsNameWrapper,
  MofiferOptionsTextWrapper,
  MofiferOptionsPriceText,
  MofiferOptionsText,
} from "./styles";
import { PRODUCT_STATUS, ORDER_STATUSES } from "src/constants";
import {
  getPathKeyLevel,
  extractParentOptionId,
} from "src/utils/pathKeyHelpers";

const styles = {
  row: {
    marginBottom: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  countCell: {
    textAlign: "left",
  },
  nameCell: {
    textAlign: "left",
    padding: "0 14px",
    fontSize: "14px",
    fontWeight: 600,
  },
  priceCell: {
    textAlign: "right",
  },
};

const OrderProduct = ({
  product,
  charges,
  promotions = [],
  showVoidItemReceipt = true,
}) => {
  const { count, name, modifierGroup, singlePriceWithoutModifier, price } =
    product || {};

  const containerRef = useRef(null);
  const priceRef = useRef(null);
  const modPriceRef = useRef(null);
  const [nameWidth, setNameWidth] = useState("100%");

  const itemDiscount = useMemo(() => {
    return charges?.discountDetails?.find(
      (item) => item?.appliesTo === "item" && item?.itemUuid === product?.uuid
    );
  }, [charges?.discountDetails, product?.uuid]);

  const itemCoupon = itemDiscount?.couponId ? itemDiscount : null;

  const visiblePromotions = useMemo(() => {
    const coveredLabels = new Set(
      (charges?.discountDetails || [])
        .filter(
          (item) =>
            item?.appliesTo === "item" &&
            item?.itemUuid === product?.uuid &&
            item?.couponId
        )
        .flatMap((item) => [
          item.couponTitle,
          item.couponName,
          item.promotionName,
          item.discountCode,
          item.couponCode,
        ])
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean)
    );

    return (promotions || []).filter((adjustment) => {
      const labels = [
        adjustment.displayLabel,
        adjustment.promotionName,
        adjustment.promotionCode,
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean);
      return !labels.some((label) => coveredLabels.has(label));
    });
  }, [charges?.discountDetails, product?.uuid, promotions]);

  const line = useMemo(() => buildRollupLine(product), [product]);
  const shownLineTotal = line.useLineTotal
    ? line.lineTotal
    : Number(singlePriceWithoutModifier || 0);
  const mainPrice = valueFormat(`$${Number(shownLineTotal).toFixed(2)}`);
  const discountBase = line.useLineTotal ? line.lineTotal : Number(price || 0);
  const discountPrice =
    itemDiscount?.discountAmount &&
    valueFormat(
      `$${Number(discountBase - Math.abs(itemDiscount?.discountAmount || 0)).toFixed(
        2
      )}`
    );
  const isVoid = product?.status?.name === PRODUCT_STATUS.VOID;
  const itemNote = product?.notes || product?.instructions || "";

  useEffect(() => {
    if (!containerRef.current || !priceRef.current) return;

    const update = () => {
      const container = containerRef.current;
      const price = priceRef.current;
      const containerWidth = container?.offsetWidth;
      const priceWidth = price?.offsetWidth;
      const available = containerWidth - priceWidth; // include safe padding
      setNameWidth(`${available}px`);
    };

    update(); // run once initially

    const resizeObserver = new ResizeObserver(() => {
      update(); // update anytime size changes
    });

    resizeObserver.observe(containerRef.current);
    resizeObserver.observe(priceRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef?.current, priceRef?.current, modPriceRef?.current]);

  const shouldShowStatusDisplayName = useMemo(() => {
    return (
      product?.status?.name === ORDER_STATUSES.COMP_AFTER_TXN ||
      product?.status?.name === ORDER_STATUSES.COMP_BEFORE_TXN
    );
  }, [product?.status?.name]);

  // Convert modifierOptions to array if it's an object
  const getModifierOptionsArray = (modifierOptions) => {
    if (!modifierOptions) return [];
    if (Array.isArray(modifierOptions)) return modifierOptions;
    if (typeof modifierOptions === "object") {
      return Object.values(modifierOptions);
    }
    return [];
  };

  // Constants matching React Native implementation
  const VISUAL_INDENT = 12; // Reduced from 20 for smaller indentation
  const BASE_INDENT = 16; // Reduced from 22 for smaller base indent

  // Build set of full option pathKeys that are parents (have children)
  const parentOptionPathKeys = useMemo(() => {
    const parentPaths = new Set();

    if (!modifierGroup || !Array.isArray(modifierGroup)) {
      return parentPaths;
    }

    for (const group of modifierGroup) {
      // Try to get pathKey from multiple sources
      let pathKey = group.pathKey || "";
      if (!pathKey && group.metadata?.pathKey) {
        pathKey = group.metadata.pathKey;
      }
      if (!pathKey && group._id) {
        const productId = product?._id || product?.id || product?.uuid || "";
        if (productId) {
          pathKey = `product_${productId}__mg_${group._id}`;
        } else {
          pathKey = `mg_${group._id}`;
        }
      }
      if (!pathKey) continue;

      // Find the parent option pathKey by removing the last __mg_xxx segment
      const lastMgIndex = pathKey.lastIndexOf("__mg_");
      if (lastMgIndex > 0) {
        const parentOptionPathKey = pathKey.substring(0, lastMgIndex);
        // Only add if it ends with __op_xxx (i.e., it's an option path, not product)
        if (parentOptionPathKey.includes("__op_")) {
          parentPaths.add(parentOptionPathKey);
        }
      }
    }

    return parentPaths;
  }, [modifierGroup, product?._id, product?.id, product?.uuid]);

  // Build modifier group data with level and parent option info (flat list, not tree)
  const modifierGroupData = useMemo(() => {
    if (
      !modifierGroup ||
      !Array.isArray(modifierGroup) ||
      modifierGroup.length === 0
    ) {
      return [];
    }

    const productId = product?._id || product?.id || product?.uuid || "";

    // Build a map of option ID to option data (count, price, name) from all modifier groups
    const optionDataMap = {};
    for (const group of modifierGroup) {
      const options = getModifierOptionsArray(group.modifierOptions);
      for (const opt of options) {
        if (opt && opt._id) {
          optionDataMap[opt._id] = {
            count: opt.count || 1,
            price: opt.totalPrice || opt.price || 0,
            name: opt.name?.en || opt.name || "",
          };
        }
      }
    }

    // Build groups with level and parent option info
    const groupsWithLevel = modifierGroup.map((group) => {
      // Try to get pathKey from multiple sources (like React Native version)
      // IMPORTANT: pathKey should be directly on the group object from API
      // Check pathKey directly on group first (most common case)
      let pathKey =
        group.pathKey && typeof group.pathKey === "string" ? group.pathKey : "";

      // If pathKey is not directly on the group, try metadata
      if (!pathKey && group.metadata?.pathKey) {
        pathKey = group.metadata.pathKey;
      }

      // If still no pathKey, try to construct it (fallback for level 0 groups only)
      // Only construct if pathKey is truly missing - don't override existing pathKeys
      if (!pathKey && group._id) {
        if (productId) {
          pathKey = `product_${productId}__mg_${group._id}`;
        } else {
          pathKey = `mg_${group._id}`;
        }
      }

      // Calculate level based on pathKey
      const level = getPathKeyLevel(pathKey);

      // Debug: Log pathKey and level to verify calculation
      // console.log(`Group: ${group.name?.en || group.name}, pathKey: ${pathKey}, level: ${level}`);

      // Extract parent option info
      let parentOptionName;
      let parentOptionCount;
      let parentOptionPrice;
      if (level > 0 && pathKey) {
        const parentOptionId = extractParentOptionId(pathKey);
        if (parentOptionId) {
          const parentOptionData = optionDataMap[parentOptionId];
          if (parentOptionData) {
            parentOptionName = parentOptionData.name;
            parentOptionCount = parentOptionData.count;
            parentOptionPrice = parentOptionData.price;
          }
        }
      }

      const groupWithLevel = {
        ...group,
        level,
        pathKey, // Ensure pathKey is preserved
        parentOptionName,
        parentOptionCount,
        parentOptionPrice,
      };

      return groupWithLevel;
    });

    // Find nested MGs for a given option pathKey prefix
    const findNestedMGs = (optionPathPrefix) => {
      const nested = [];
      for (const mg of groupsWithLevel) {
        const pk = mg.pathKey || "";
        if (
          pk.startsWith(optionPathPrefix + "__mg_") &&
          pk.split("__").length === optionPathPrefix.split("__").length + 1
        ) {
          nested.push(mg);
        }
      }
      return nested;
    };

    // Recursive function to add MGs in correct order
    const result = [];
    const processed = new Set();

    const addMGRecursively = (mg) => {
      if (processed.has(mg.pathKey)) return;
      processed.add(mg.pathKey);
      result.push(mg);

      // For each option, check for nested MGs
      const options = getModifierOptionsArray(mg.modifierOptions);
      for (const opt of options) {
        const optionPathKey = `${mg.pathKey}__op_${opt._id || ""}`;
        const nestedMGs = findNestedMGs(optionPathKey);
        for (const nestedMG of nestedMGs) {
          addMGRecursively(nestedMG);
        }
      }
    };

    // Process level 0 groups first (in their original order)
    for (const mg of groupsWithLevel) {
      if (mg.level === 0) {
        addMGRecursively(mg);
      }
    }

    // Add any remaining groups
    for (const mg of groupsWithLevel) {
      if (!processed.has(mg.pathKey)) {
        result.push(mg);
      }
    }

    return result;
  }, [modifierGroup, product?._id, product?.id, product?.uuid]);

  // Render modifier group item (matching React Native ModifierGroupItem)
  const renderModifierGroupItem = (modifier, index, isFirstWithParent) => {
    const options = getModifierOptionsArray(modifier.modifierOptions);

    // Filter out options that have children (they will be shown as parent headers)
    const modifierGroupDataOptions = options.filter((opt) => {
      const optionPathKey = `${modifier.pathKey}__op_${opt._id || ""}`;
      return !parentOptionPathKeys.has(optionPathKey);
    });

    // Add BASE_INDENT so even level 0 groups are indented from product name
    const indent = BASE_INDENT + modifier.level * VISUAL_INDENT;
    // Parent option should be at parent modifier group level (pulled back by VISUAL_INDENT)
    const parentHeaderMargin = -VISUAL_INDENT;

    // Only show parent header if this is a child group AND it's the first group with this parent
    const shouldShowParent =
      modifier.parentOptionName && modifier.level > 0 && isFirstWithParent;

    return (
      <Fragment key={`${modifier._id}-${index}`}>
        {/* Parent option header - at parent modifier group level */}
        {shouldShowParent && (
          <MofiferOptionsNameWrapper
            sx={{
              textDecoration: isVoid ? "line-through" : "none",
              marginLeft: `${indent + parentHeaderMargin}px`,
              marginBottom: "2px",
              width: `calc(100% - ${indent + parentHeaderMargin}px)`, // Adjust width to prevent overflow
              minWidth: 0, // Allow flex shrinking
            }}
          >
            <MofiferOptionsTextWrapper
              sx={{
                maxWidth: "100%",
                flex: "1 1 auto",
                minWidth: 0, // Allow text truncation
              }}
            >
              <MofiferOptionsText
                noWrap
                sx={{
                  paddingLeft: "0px", // Remove base padding to align properly
                }}
              >
                <b>{`${modifier.parentOptionCount || 1}x `}</b>
                <span>{modifier.parentOptionName}</span>
              </MofiferOptionsText>
            </MofiferOptionsTextWrapper>
            {Number(modifier.parentOptionPrice || 0) > 0 ? (
            <MofiferOptionsPriceText
              sx={{
                textDecoration: isVoid ? "line-through" : "none",
              }}
            >
              {valueFormat(
                `+$${Number(modifier.parentOptionPrice || 0).toFixed(2)}`
              )}
            </MofiferOptionsPriceText>
            ) : null}
          </MofiferOptionsNameWrapper>
        )}

        {/* Modifier group name */}
        {modifier.hideNameOnOrder === true ? null : (
        <MofiferNameWrapper
          className="modifier-main-name modifier-name-longer"
          sx={{
            textDecoration: isVoid ? "line-through" : "none",
            marginLeft: `${indent}px`,
            fontWeight: 700,
          }}
        >
          {modifier.name?.en || modifier.name}
        </MofiferNameWrapper>
        )}

        {/* Options */}
        {modifierGroupDataOptions.map((optionItem, optIndex) => {
          const option = formatModifierOption(optionItem, modifier);
          if (!option) return null;
          return (
            <Fragment key={`${optionItem._id}-${optIndex}`}>
            <MofiferOptionsNameWrapper
              sx={{
                textDecoration: isVoid ? "line-through" : "none",
                marginLeft: `${indent}px`, // Same indent as parent group
                paddingLeft: "0px", // Remove base padding to align with group name
                width: `calc(100% - ${indent}px)`, // Adjust width to prevent overflow
                minWidth: 0, // Allow flex shrinking
              }}
            >
              <MofiferOptionsTextWrapper
                sx={{
                  maxWidth: "100%",
                  flex: "1 1 auto",
                  minWidth: 0, // Allow text truncation
                }}
              >
                <MofiferOptionsText
                  noWrap
                  sx={{
                    paddingLeft: "0px", // Remove base padding to align with group name
                  }}
                >
                  {option.showQty ? <b>{`${option.qtyLabel} `}</b> : null}
                  <span>{option.name}</span>
                </MofiferOptionsText>
              </MofiferOptionsTextWrapper>
              {option.priceLabel ? (
              <MofiferOptionsPriceText
                ref={modPriceRef}
                sx={{
                  textDecoration:
                    isVoid || itemDiscount?.discountAmount > 0
                      ? "line-through"
                      : "none",
                }}
              >
                {option.priceLabel}
              </MofiferOptionsPriceText>
              ) : null}
            </MofiferOptionsNameWrapper>
            {option.explanation ? (
              <Typography
                sx={{
                  fontSize: "13px",
                  marginLeft: `${indent}px`,
                  textDecoration: isVoid ? "line-through" : "none",
                }}
              >
                {option.explanation}
              </Typography>
            ) : null}
            </Fragment>
          );
        })}
      </Fragment>
    );
  };

  // If item is void and showVoidItemReceipt is false, don't render the item
  if (isVoid && !showVoidItemReceipt) {
    return null;
  }

  return (
    <>
      <Box sx={styles.row} ref={containerRef}>
        <NamePriceRowWrapper
          sx={{
            maxWidth: nameWidth,
            textDecoration: isVoid ? "line-through" : "none",
          }}
        >
          <Typography sx={{ ...styles.countCell, width: 36, flexShrink: 0 }}>{`${count}×`}</Typography>
          <Typography
            className="uppercase"
            noWrap
            sx={{
              ...styles.nameCell,
              maxWidth: nameWidth,
            }}
          >
            {name?.en}
          </Typography>
        </NamePriceRowWrapper>

        <Box ref={priceRef}>
          <Typography
            className="uppercase"
            sx={{
              textDecoration:
                itemDiscount?.discountAmount > 0 || isVoid
                  ? "line-through"
                  : "none",
              width: priceRef?.current?.offsetWidth,
            }}
          >
            {mainPrice}
          </Typography>
        </Box>
      </Box>
      {line.description ? (
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            paddingLeft: "44px",
            textDecoration: isVoid ? "line-through" : "none",
          }}
        >
          {line.description}
        </Typography>
      ) : null}
      {shouldShowStatusDisplayName && product?.status?.displayName ? (
        <Typography
          sx={{ fontSize: "14px", color: "gray", fontStyle: "italic" }}
        >
          ({product?.status?.displayName})
        </Typography>
      ) : null}
      {modifierGroupData.length > 0 &&
        (() => {
          // Track which parent options have already been shown (using pathKey prefix as unique key)
          const shownParents = new Set();
          return modifierGroupData.map((modifier, index) => {
            // Use pathKey prefix (all segments except last) as unique parent identifier
            const pathParts = (modifier.pathKey || "").split("__");
            const parentPathKey =
              pathParts.length > 1 ? pathParts.slice(0, -1).join("__") : "";
            const parentKey = parentPathKey || "";
            const isFirstWithParent =
              parentKey === "" || !shownParents.has(parentKey);
            if (parentKey) {
              shownParents.add(parentKey);
            }

            return renderModifierGroupItem(modifier, index, isFirstWithParent);
          });
        })()}

      {itemCoupon?.discountAmount > 0 ? (
        <DiscountRowWrapper>
          <DiscountRowTextWrapper
            sx={{ textDecoration: isVoid ? "line-through" : "none" }}
          >
            <DiscountRowLabelWrapper
              sx={{
                ...styles.nameCell,
                fontSize: "16px",
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {couponDetailLabel(itemCoupon)}
              </span>
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>
          <DiscountRowPriceWrapper
            sx={{
              ...styles.priceCell,
              alignItems: "baseline",
              fontSize: "16px",
            }}
          >
            <span style={{ fontWeight: 500 }}>
              {valueFormat(
                `($${Number(itemCoupon.discountAmount || 0).toFixed(2)})`
              )}
            </span>
          </DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ) : itemDiscount?.discountAmount > 0 ? (
        <DiscountRowWrapper>
          <DiscountRowTextWrapper
            sx={{ textDecoration: isVoid ? "line-through" : "none" }}
          >
            <DiscountRowLabelWrapper
              sx={{
                ...styles.nameCell,
                fontSize: "16px",
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {itemCheckDiscountLabel(itemDiscount)}
              </span>
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>

          <DiscountRowPriceWrapper
            sx={{
              ...styles.priceCell,
              alignItems: "baseline",
              fontSize: "16px",
            }}
          >
            <span style={{ fontWeight: 500 }}>{discountPrice}</span>
          </DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ) : null}
      {visiblePromotions.map((adjustment, index) => (
        <DiscountRowWrapper
          key={`${adjustment.promotionCode || adjustment.promotionName || 'promotion'}-${index}`}
        >
          <DiscountRowTextWrapper sx={{ textDecoration: isVoid ? 'line-through' : 'none' }}>
            <DiscountRowLabelWrapper
              sx={{
                ...styles.nameCell,
                fontSize: '16px',
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {promotionAdjustmentLabel(adjustment)}
              </span>
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>
          <DiscountRowPriceWrapper
            sx={{
              ...styles.priceCell,
              alignItems: 'baseline',
              fontSize: '16px',
            }}
          >
            <span style={{ fontWeight: 500 }}>
              {valueFormat(
                `($${Number(adjustment.discountAmount || 0).toFixed(2)})`
              )}
            </span>
          </DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ))}
      {itemNote ? (
        <Box>
          <Box sx={{ marginLeft: "30px" }}>
            <Typography
              style={{ textDecoration: isVoid ? "line-through" : "none" }}
            >
              NOTES:
            </Typography>
            {itemNote?.split("\n")?.map((item, index) => {
              if (!item) return null;
              return (
                <React.Fragment key={index}>
                  <Typography
                    sx={{
                      maxWidth: "360px",
                      textDecoration: isVoid ? "line-through" : "none",
                    }}
                  >
                    {item}
                  </Typography>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </>
  );
};

export default memo(OrderProduct);
